import styled from "@emotion/styled";
import { DropdownMenu as DropdownMenuPrimitive } from "radix-ui";
import React from "react";

import { Icon } from "@components/Icon";
import { theme } from "@themes/themes";

const Root = DropdownMenuPrimitive.Root;
const Trigger = DropdownMenuPrimitive.Trigger;
const Group = DropdownMenuPrimitive.Group;
const Portal = DropdownMenuPrimitive.Portal;
const Sub = DropdownMenuPrimitive.Sub;
const RadioGroup = DropdownMenuPrimitive.RadioGroup;

const StyledContent = styled(DropdownMenuPrimitive.Content)`
  z-index: 50;
  min-width: 8rem;
  overflow: hidden;
  border-radius: 0.375rem;
  padding: 0.25rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

  &[data-state="open"] {
    animation:
      fadeIn 0.2s ease,
      zoomIn 0.2s ease;
  }

  &[data-state="closed"] {
    animation:
      fadeOut 0.2s ease,
      zoomOut 0.2s ease;
  }
`;

const StyledSubTrigger = styled(DropdownMenuPrimitive.SubTrigger)<{ inset?: boolean }>`
  display: flex;
  align-items: center;
  cursor: default;
  user-select: none;
  padding: 0.375rem 0.5rem;
  font-size: 0.875rem;
  border-radius: 0.375rem;
  outline: none;

  ${({ inset }) => inset && "padding-left: 2rem;"}

  &:focus,
  &[data-state="open"] {
    background: ${theme.color("accent-7")};
  }
`;

const StyledSubContent = styled(DropdownMenuPrimitive.SubContent)`
  z-index: 50;
  min-width: 8rem;
  overflow: hidden;
  border-radius: 0.375rem;
  padding: 0.25rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const StyledItem = styled(DropdownMenuPrimitive.Item)<{ inset?: boolean }>`
  display: flex;
  align-items: center;
  cursor: default;
  user-select: none;
  padding: 0.375rem 0.5rem;
  font-size: 0.875rem;
  border-radius: 0.375rem;
  outline: none;
  transition: background 0.2s;
  position: relative;

  ${({ inset }) => inset && "padding-left: 2rem;"}

  &:focus {
    background: ${theme.color("accent-7")};
    color: var(--accent-foreground);
  }

  &[data-disabled] {
    pointer-events: none;
    opacity: 0.5;
  }
`;

const StyledCheckboxItem = styled(DropdownMenuPrimitive.CheckboxItem)`
  display: flex;
  align-items: center;
  cursor: default;
  user-select: none;
  padding: 0.375rem 0.5rem;
  padding-left: 2rem;
  font-size: 0.875rem;
  border-radius: 0.375rem;
  outline: none;
  position: relative;

  &:focus {
    background: ${theme.color("accent-7")};
    color: var(--accent-foreground);
  }

  &[data-disabled] {
    pointer-events: none;
    opacity: 0.5;
  }
`;

const StyledRadioItem = styled(DropdownMenuPrimitive.RadioItem)`
  display: flex;
  align-items: center;
  cursor: default;
  user-select: none;
  padding: 0.375rem 0.5rem;
  padding-left: 2rem;
  font-size: 0.875rem;
  border-radius: 0.375rem;
  outline: none;
  position: relative;

  &[data-disabled] {
    pointer-events: none;
    opacity: 0.5;
  }
`;

const IndicatorWrapper = styled("span")`
  position: absolute;
  left: 0.5rem;
  display: flex;
  height: 0.875rem;
  width: 0.875rem;
  align-items: center;
  justify-content: center;
`;

const StyledLabel = styled(DropdownMenuPrimitive.Label)<{ inset?: boolean }>`
  font-size: 0.875rem;
  font-weight: 600;
  padding: 0.375rem 0.5rem;

  ${({ inset }) => inset && "padding-left: 2rem;"}
`;

const StyledSeparator = styled(DropdownMenuPrimitive.Separator)`
  height: 1px;
  margin: 0.25rem 0;
`;

const Shortcut = styled("span")`
  margin-left: auto;
  font-size: 0.75rem;
  letter-spacing: 0.1em;
  opacity: 0.6;
`;

const Content = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>
>(({ sideOffset = 4, ...props }, ref) => (
  <DropdownMenuPrimitive.Portal>
    <StyledContent ref={ref} sideOffset={sideOffset} {...props} />
  </DropdownMenuPrimitive.Portal>
));
Content.displayName = "DropdownContent";

const SubContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubContent>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent>
>((props, ref) => <StyledSubContent ref={ref} {...props} />);
SubContent.displayName = "DropdownSubContent";

const SubTrigger = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubTrigger>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger> & {
    inset?: boolean;
  }
>(({ children, inset, ...props }, ref) => (
  <StyledSubTrigger ref={ref} inset={inset} {...props}>
    {children}
    <Icon icon="arrow-right-s-line" size={16} />
  </StyledSubTrigger>
));
SubTrigger.displayName = "DropdownSubTrigger";

const Item = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & {
    inset?: boolean;
  }
>((props, ref) => <StyledItem ref={ref} {...props} />);
Item.displayName = "DropdownItem";

const CheckboxItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.CheckboxItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.CheckboxItem>
>(({ children, ...props }, ref) => (
  <StyledCheckboxItem ref={ref} {...props}>
    <IndicatorWrapper>
      <DropdownMenuPrimitive.ItemIndicator>
        <Icon icon="ri-check-line" size={16} />
      </DropdownMenuPrimitive.ItemIndicator>
    </IndicatorWrapper>
    {children}
  </StyledCheckboxItem>
));
CheckboxItem.displayName = "DropdownCheckboxItem";

const RadioItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.RadioItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioItem>
>(({ children, ...props }, ref) => (
  <StyledRadioItem ref={ref} {...props}>
    <IndicatorWrapper>
      <DropdownMenuPrimitive.ItemIndicator>
        <Icon icon="ri-circle-line" size={8} />
      </DropdownMenuPrimitive.ItemIndicator>
    </IndicatorWrapper>
    {children}
  </StyledRadioItem>
));
RadioItem.displayName = "DropdownRadioItem";

const Label = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label> & {
    inset?: boolean;
  }
>((props, ref) => <StyledLabel ref={ref} {...props} />);
Label.displayName = "DropdownLabel";

const Separator = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>((props, ref) => <StyledSeparator ref={ref} {...props} />);
Separator.displayName = "DropdownSeparator";

export const Dropdown = {
  Root,
  CheckboxItem,
  Content,
  Group,
  Item,
  Label,
  Portal,
  RadioGroup,
  RadioItem,
  Separator,
  Shortcut,
  Sub,
  SubContent,
  SubTrigger,
  Trigger,
};
